/**
 * 백엔드 헬스 폴링 — 주기적으로 /api/health 를 호출해 서버 상태 감시.
 * 연속 실패 N 회 이상 시 isDown=true, 한 번이라도 성공 시 즉시 isDown=false.
 *
 * 부하 억제 규칙 (서버 보호):
 *  1) 숨김 탭(document.hidden)에서는 폴링 중단 → 탭 N 개 열어도 서버 부하가 N 배가 되지 않음.
 *     탭 복귀 시 즉시 1 회 확인 후 재개.
 *  2) 실패 시 "가속"이 아니라 지수 백오프(5s → 10s → 20s → 40s, 상한 60s).
 *     장애 중인 서버를 더 두들겨 장애를 증폭시키던 구조 제거. 성공하면 기본 주기로 복귀.
 *  3) 대시보드(/dashboard/*) 밖의 공개 페이지(랜딩·블로그·사고데이터센터·로그인 등)에서는 비활성.
 *     로그인하지 않은 SEO 유입 방문자가 헬스 폴링을 발생시키지 않게 한다.
 */
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { api } from '../lib/api'

interface Options {
  intervalMs?: number         // 성공 상태 기본 폴링 주기 (default 30s)
  failIntervalMs?: number     // 첫 실패 후 재시도 간격 (백오프 기준값, default 5s)
  maxFailIntervalMs?: number  // 백오프 상한 (default 60s)
  failThreshold?: number      // 연속 실패 회수 임계 (default 3)
  timeoutMs?: number          // 한 번 폴링 타임아웃 (default 6s)
  /**
   * 폴링 활성 여부. 미지정 시 경로 기반 자동 판별(대시보드에서만 폴링).
   * 호출부에서 명시하면 그 값이 우선한다.
   */
  enabled?: boolean
}

/** 헬스 폴링이 의미 있는 경로인지 — 로그인 사용자가 쓰는 대시보드 계열만. */
function isMonitoredPath(pathname: string): boolean {
  return pathname === '/dashboard' || pathname.startsWith('/dashboard/')
}

export function useBackendHealth({
  intervalMs = 30_000,
  failIntervalMs = 5_000,
  maxFailIntervalMs = 60_000,
  failThreshold = 3,
  timeoutMs = 6_000,
  enabled,
}: Options = {}) {
  const [isDown, setIsDown] = useState(false)
  const [lastOkAt, setLastOkAt] = useState<number | null>(null)
  const failCountRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // SPA 내비게이션에도 반응하도록 라우터 경로를 사용(window.location 은 라우팅 시 갱신 감지가 안 됨).
  // AppRoot 는 라우트 엘리먼트이므로 라우터 컨텍스트 안이다.
  const { pathname } = useLocation()
  const active = enabled ?? isMonitoredPath(pathname)

  useEffect(() => {
    // 공개 페이지 등 비활성 구간 — 폴링하지 않고 상태도 정상으로 되돌린다.
    if (!active) {
      failCountRef.current = 0
      setIsDown(false)
      return
    }

    let cancelled = false

    /** 다음 폴링까지 대기 시간 — 성공 시 기본 주기, 실패 시 지수 백오프(상한 적용). */
    const nextDelay = () => {
      const fails = failCountRef.current
      if (fails === 0) return intervalMs
      const backoff = failIntervalMs * 2 ** (fails - 1)
      return Math.min(backoff, maxFailIntervalMs)
    }

    const schedule = (delay: number) => {
      if (cancelled) return
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(poll, delay)
    }

    const poll = async () => {
      if (cancelled) return

      // 숨김 탭이면 폴링을 건너뛰고 타이머도 걸지 않는다.
      // 복귀는 visibilitychange 핸들러가 담당 → 백그라운드 탭은 완전히 조용해진다.
      if (document.visibilityState === 'hidden') return

      try {
        await api.get('/health', { timeout: timeoutMs, withCredentials: false })
        if (cancelled) return
        failCountRef.current = 0
        setLastOkAt(Date.now())
        setIsDown(false)
      } catch {
        if (cancelled) return
        failCountRef.current += 1
        if (failCountRef.current >= failThreshold) {
          setIsDown(true)
        }
      } finally {
        // ⚠️ await 를 건넜으므로 그 사이에 탭이 숨겨졌을 수 있다 — 여기서 '다시' 확인해야 한다.
        //    (82행의 조기반환 때문에 TS 는 visibilityState 를 'visible' 로 좁혀 TS2367 을 낸다.
        //     TS 는 await 를 넘나드는 값 변화를 모른다 → boolean 인 document.hidden 으로 확인한다.
        //     의미는 동일하고, 숨김 탭에 타이머가 남는 것을 막는 핵심 가드다.)
        if (!cancelled && !document.hidden) {
          schedule(nextDelay())
        }
      }
    }

    // 첫 호출 (초기 500ms 지연 — 다른 부트스트랩 요청과 겹치지 않게)
    if (document.visibilityState !== 'hidden') schedule(500)

    // 탭 복귀 시 즉시 1 회 확인 후 폴링 재개. 숨겨지면 대기 중인 타이머 해제.
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = null
      } else {
        void poll()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    // 창 포커스 복귀 시, 장애 상태였다면 즉시 재확인(백오프 대기 단축).
    const handleFocus = () => {
      if (failCountRef.current > 0 && document.visibilityState !== 'hidden') void poll()
    }
    window.addEventListener('focus', handleFocus)

    return () => {
      cancelled = true
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = null
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('focus', handleFocus)
    }
  }, [active, intervalMs, failIntervalMs, maxFailIntervalMs, failThreshold, timeoutMs])

  return { isDown, lastOkAt }
}
